/**
 * RendPleX Scenario Analyzer 2.0 — P1A session storage for A/B/C slots.
 *
 * Design guarantees:
 *   • Storage is per-user: key = `rendplex_scenarios_session_v1:<user_id>`.
 *   • Never persisted to Mongo. Never attached to the Deal.
 *   • Cleared on logout / account change via clearAllScenarioSessions().
 *   • Fails silently if sessionStorage is unavailable (private mode, SSR).
 */

const KEY_PREFIX = 'rendplex_scenarios_session_v1:';
const INDEX_KEY = 'rendplex_scenarios_session_v1__users';

const EMPTY_SLOTS = Object.freeze({ A: null, B: null, C: null });

function _hasStorage() {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage;
  } catch (_) { return false; }
}

function _userKey(userId) {
  return `${KEY_PREFIX}${userId || 'anon'}`;
}

function _updateIndex(userId, add) {
  if (!_hasStorage()) return;
  try {
    const raw = window.sessionStorage.getItem(INDEX_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const set = new Set(list);
    if (add) set.add(userId); else set.delete(userId);
    window.sessionStorage.setItem(INDEX_KEY, JSON.stringify([...set]));
  } catch (_) { /* silent */ }
}

/** Return {A,B,C} slot state for the user, or empty slots. */
export function loadSlots(userId) {
  if (!_hasStorage() || !userId) return { ...EMPTY_SLOTS };
  try {
    const raw = window.sessionStorage.getItem(_userKey(userId));
    if (!raw) return { ...EMPTY_SLOTS };
    const parsed = JSON.parse(raw);
    return {
      A: parsed.A || null,
      B: parsed.B || null,
      C: parsed.C || null,
    };
  } catch (_) {
    return { ...EMPTY_SLOTS };
  }
}

/** Persist {A,B,C} slot state for the user. */
export function saveSlots(userId, slots) {
  if (!_hasStorage() || !userId) return;
  try {
    window.sessionStorage.setItem(_userKey(userId), JSON.stringify({
      A: slots.A || null,
      B: slots.B || null,
      C: slots.C || null,
    }));
    _updateIndex(userId, true);
  } catch (_) { /* silent */ }
}

/** Wipe A/B/C for one user (used by "Clear all A/B/C" confirmed action). */
export function clearSlots(userId) {
  if (!_hasStorage() || !userId) return;
  try {
    window.sessionStorage.removeItem(_userKey(userId));
    _updateIndex(userId, false);
  } catch (_) { /* silent */ }
}

/** Wipe scenarios for every known user in this browser session.
 *  Called on logout so signing back in as a different user cannot
 *  leak the previous user's scenario slots. */
export function clearAllScenarioSessions() {
  if (!_hasStorage()) return;
  try {
    const raw = window.sessionStorage.getItem(INDEX_KEY);
    const list = raw ? JSON.parse(raw) : [];
    for (const uid of list) {
      window.sessionStorage.removeItem(_userKey(uid));
    }
    window.sessionStorage.removeItem(INDEX_KEY);
  } catch (_) { /* silent */ }
}
